// REACT
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// YARN
import { toast } from 'react-toastify';
// SERVICES
import CustomDataGrid from 'utils/Datagrid/CustomCultureDataGrid';
import {
  inputAbsolutePositiveFloatValidator,
  inputNumberRequired,
  inputPositiveFloatValidator,
  inputRequired,
  parcelConversionDateChecker,
} from 'components/generics/Validators/validator';
import { CustomHeaderWithoutSort, EditInputCell } from 'utils/Datagrid/CustomCultureDataGridUtils';
import {
  CustomSortableHeader,
  childrenTotalSurface,
  getFamily,
  processColumns,
  totalSurfaceComparator,
  resetErrorsForm,
} from 'utils/Datagrid/CustomGenericDataGridUtils';
import useDataGridParcelsActions from 'utils/Datagrid/useDataGridParcelsActions';
import {
  checkDecimalRegexTwoDigitsDatagridValue,
  checkTypeStringRegexDatagrid,
  getOnlyAttributeUpdated,
  previousCultureRange,
  sortByLocalName,
} from 'utils/tools_functions';
import { validateDatagridData } from '../../generics/Validators/datagrid.validate';
import { isSameAsOriginal } from 'utils/Datagrid/CustomForecastDataGridUtils';
import {
  CustomConversionDateParcelCell,
  CustomEditConversionDateCell,
  CustomEditOrganicStateCell,
  CustomParcelCell,
  CustomParcelSurfaceCell,
  customIsIrrigableCell,
  customIsOrganicCell,
  getCultureCampaignKey,
} from 'utils/Datagrid/CustomParcelstDataGridUtils';
import { ORGANIC_STATES } from 'utils/GlobalVariables';
// THEMES
import './dataGridParcels.style.scss';

/**
 * @typedef {import('components/generics/Interface/ICulture').ICulture} ICulture
 * @typedef {import('components/generics/Interface/Api/Response/GroundType/IGroundType').IGroundType} IGroundType
 * @typedef {import('components/generics/Interface/Exploitation/Parcels/ICampaign').ICampaign} ICampaign
 * @typedef {import('components/generics/Interface/Api/Response/Exploitation/Parcels/IParcelsList').IParcelsList} IParcelsList
 * @typedef {import('components/generics/Interface/Api/Response/Metadata/IMetadata').IMetadata} IMetadata
 * @typedef {import('components/generics/Interface/Exploitation/Parcels/IStackedParcelsRequestsRef').IStackedParcelsRequestsRef} IStackedParcelsRequestsRef
 */

/**
 * Row datagrid {@link IParcelsList}
 * @typedef {Object} ProcessedRow
 * @property {number} id
 * @property {string} name
 * @property {number} surface
 * @property {number|undefined} totalSurface
 * @property {number} exploitationId
 * @property {string|undefined} conversionDate
 * @property {string|undefined} organicState
 * @property {boolean} isIrrigable
 * @property {string} groundType
 * @property {string} previousCulture5
 * @property {string} previousCulture4
 * @property {string} previousCulture3
 * @property {string} previousCulture2
 * @property {string} previousCulture1
 * @property {number[]|undefined} parentId
 * @property {boolean} isParent
 * @property {number|undefined} activityStart
 * @property {number|undefined} activityEnd
 */

/**
 * Tableau des parcelles d'une exploitation
 *
 * @param {Object} props
 * @param {number} props.id
 * @param {IGroundType[]} props.groundTypes
 * @param {ICulture[]} props.cultures
 * @param {ICampaign} props.forecastCampaign
 * @param {IParcelsList[]} [props.parcels]
 * @param {Function} props.refreshData
 * @param {boolean} props.dataGridLoading
 * @param {Function} props.setEditData
 * @param {Function} [props.setModalGroup]
 * @param {IMetadata[]} props.columnsMetadata
 * @param {React.RefObject<IStackedParcelsRequestsRef[]>} props.stackedParcelsRequestsRef
 * @param {Function} [props.setIsAllowedSubmit]
 * @param {import('dayjs').Dayjs} props.maxDate
 * @param {(id: string|number) => JSX.Element[]} [props.getActions]
 * @param {JSX.Element} [props.customToolbar]
 */
const DataGridParcels = ({
  id,
  groundTypes,
  cultures,
  forecastCampaign,
  parcels,
  refreshData,
  dataGridLoading,
  setEditData,
  setModalGroup,
  columnsMetadata,
  stackedParcelsRequestsRef,
  setIsAllowedSubmit,
  maxDate,
  getActions,
  customToolbar,
}) => {
  const { t } = useTranslation();
  const [sortModel, setSortModel] = useState([
    {
      field: 'name',
      sort: 'asc',
    },
  ]);
  const [errorsFormDefault, setErrorsFormDefault] = useState({
    formError: false,
    name: {
      message: '',
      validator: [inputRequired],
    },
    surface: {
      message: '',
      validator: [inputNumberRequired, inputAbsolutePositiveFloatValidator],
    },
    conversionDate: {
      message: '',
      validator: [parcelConversionDateChecker(forecastCampaign?.startYear)],
    },
  });
  const [columnsVisible, setColumnsVisible] = useState(() => {
    const hiddenCultures = {};
    previousCultureRange({ start: 3 }).forEach((offset) => {
      hiddenCultures[`previousCulture${offset}`] = false;
    });

    return {
      activityStart: false,
      activityEnd: false,
      ...hiddenCultures,
    };
  });
  const [rows, setRows] = useState([]);
  const [openOrganicStateDialog, setOpenOrganicStateDialog] = useState(false);

  const isOpen = errorsFormDefault.formError;

  /**
   * Garde de type runtime : distingue un {@link IGroundType} déjà résolu
   * d'un id de groundType non résolu sur la parcelle.
   * @param {IGroundType|number} object
   * @returns {object is IGroundType}
   */
  function isIGroundType(object) {
    return 'name' in object;
  }

  // parcel?.parcelCultures[campaignKey]?.name ?? 'Pas de culture associée',

  /** @type {(ProcessedRow|undefined)[]|undefined} */
  const processedRows = parcels?.map((parcel) => {
    if (isIGroundType(parcel.groundType)) {
      const previousCultures = {};
      previousCultureRange().forEach((offset) => {
        previousCultures[`previousCulture${offset}`] =
          parcel?.parcelCultures?.[getCultureCampaignKey(offset, forecastCampaign)]?.name ?? '-';
      });

      return {
        id: parcel?.id,
        name: parcel.name,
        surface: parcel?.surface,
        totalSurface: parcel?.isParent
          ? childrenTotalSurface(parcel?.id, parcels) + parcel?.surface
          : parcel?.surface,
        exploitationId: id,
        conversionDate: parcel?.conversionDate,
        organicState: parcel?.organicState,
        isIrrigable: parcel?.isIrrigable,
        groundType: parcel?.groundType?.name,
        ...previousCultures,
        parentId: parcel?.parent ? getFamily(parcel, parcels) : [parcel.id],
        isParent: parcel?.isParent ?? false,
        activityStart: parcel?.activityStart,
        activityEnd: parcel?.activityEnd,
      };
    }
  });

  useEffect(() => {
    if (processedRows) {
      const checkedProcessedRows = processedRows.filter((row) => row !== undefined);
      setRows(checkedProcessedRows);
    }
  }, [parcels]);

  const {
    rowModesModel,
    setRowModesModel,
    renderDeleteDialog,
    getActionsParcels,
    renderDeletegroupingDialog,
    renderOrganicStateModal,
    setModalProps,
  } = useDataGridParcelsActions(
    rows,
    refreshData,
    errorsFormDefault,
    setEditData,
    setModalGroup,
    openOrganicStateDialog,
  );

  const renderEditConversionDateCell = (params, format) => {
    return (
      <CustomEditConversionDateCell
        params={params}
        maxDate={maxDate}
        errorsFormDefault={errorsFormDefault}
        isOpen={isOpen}
        format={format}
      />
    );
  };

  // const renderEditActivityDateCell = (params) => {
  //   return (
  //     <CustomEditConversionDateCell
  //       params={params}
  //       errorsFormDefault={errorsFormDefault}
  //       isOpen={isOpen}
  //     />
  //   );
  // };

  const renderEditOrganicStateCell = (params) => {
    return (
      <CustomEditOrganicStateCell
        params={params}
        setOpenOrganicStateDialog={setOpenOrganicStateDialog}
        setModalProps={setModalProps}
      />
    );
  };
  const findRightActions = (params) => {
    if (!getActions) {
      return getActionsParcels(params.id, params.row);
    } else {
      return getActions(params.id);
    }
  };

  const columnsDefinition = [
    {
      field: 'name',
      headerName: t('common.parcel'),
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      minWidth: 180,
      flex: 1,
      sortable: false,
      editable: true,
      valueParser: (value) => {
        return checkTypeStringRegexDatagrid(value);
      },

      renderCell: (params) => CustomParcelCell(params, stackedParcelsRequestsRef),
      renderEditCell: (props) => EditInputCell(props, errorsFormDefault, isOpen),
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'name'}
          name={t('common.parcel')}
        />
      ),
    },
    {
      field: 'surface',
      headerName: t('common.surfaceHa'),
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      minWidth: 100,
      flex: 0.8,
      sortable: false,
      editable: true,
      valueParser: (value) => {
        return checkDecimalRegexTwoDigitsDatagridValue(value);
      },
      valueGetter: (value, row) => ({
        totalSurface: row?.totalSurface,
        surface: row?.surface,
      }),
      sortComparator: totalSurfaceComparator,
      renderCell: (params) => CustomParcelSurfaceCell(params, stackedParcelsRequestsRef),
      // cellClassName: (params) => (params.row.parentId.length > 1 ? 'parcel_children' : ''),
      renderEditCell: (props) => EditInputCell(props, errorsFormDefault, isOpen),
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'surface'}
          name={t('common.surfaceHa')}
        />
      ),
    },
    {
      field: 'conversionDate',
      headerName: t('column.transitionDate'),
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      type: 'string',
      editable: true,
      minWidth: 160,
      flex: 1.1,
      sortable: false,
      renderEditCell: (params) => renderEditConversionDateCell(params, 'YYYY-MM-DD'),
      renderCell: (params) => CustomConversionDateParcelCell(params, stackedParcelsRequestsRef, t),
      cellClassName: (params) => (params.row.parentId.length > 1 ? 'parcel_children' : ''),
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'conversionDate'}
          name={t('column.transitionDate')}
        />
      ),
    },
    {
      field: 'organicState',
      headerName: t('organicState.organic'),
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      type: 'boolean',
      editable: true,
      minWidth: 100,
      flex: 0.6,
      sortable: false,
      renderEditCell: renderEditOrganicStateCell,
      renderCell: (params) => customIsOrganicCell(params, stackedParcelsRequestsRef),
      cellClassName: (params) => (params.row.parentId.length > 1 ? 'parcel_children' : ''),
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'organicState'}
          name={t('organicState.organic')}
        />
      ),
      valueGetter: (value) => value === ORGANIC_STATES.organic,
      valueSetter: (value, row) => {
        return {
          ...row,
          organicState: value
            ? ORGANIC_STATES.organic
            : row?.organicState === ORGANIC_STATES.conversion
              ? ORGANIC_STATES.conversion
              : ORGANIC_STATES.conventional,
        };
      },
    },
    {
      field: 'isIrrigable',
      headerName: t('common.irrigability'),
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      type: 'boolean',
      editable: true,
      minWidth: 100,
      flex: 0.6,
      sortable: false,
      renderCell: (params) => customIsIrrigableCell(params, stackedParcelsRequestsRef),
      cellClassName: (params) => (params.row.parentId.length > 1 ? 'parcel_children' : ''),
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'isIrrigable'}
          name={t('common.irrigability')}
        />
      ),
    },
    {
      field: 'groundType',
      headerName: t('column.soilTypes'),
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      minWidth: 160,
      flex: 1,
      sortable: false,
      editable: true,
      type: 'singleSelect',
      cellClassName: (params) => (params.row.parentId.length > 1 ? 'parcel_children' : ''),

      valueOptions: () => {
        if (groundTypes) {
          return [...sortByLocalName(groundTypes)?.map((groundType) => groundType.name)];
        }
        return [];
      },
      renderCell: (params) => CustomParcelCell(params, stackedParcelsRequestsRef),
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'groundType'}
          name={t('column.soilTypes')}
        />
      ),
    },
    {
      field: 'activityStart',
      headerName: t('column.activityStartDate'),
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      type: 'string',
      editable: true,
      minWidth: 160,
      flex: 1.1,
      sortable: false,
      renderEditCell: (params) => renderEditConversionDateCell(params, 'YYYY'),
      renderCell: (params) => CustomConversionDateParcelCell(params, stackedParcelsRequestsRef, t),
      cellClassName: (params) => (params.row.parentId.length > 1 ? 'parcel_children' : ''),
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'activityStart'}
          name={t('column.activityStartDate')}
        />
      ),
    },
    {
      field: 'activityEnd',
      headerName: t('column.activityEndDate'),
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      type: 'string',
      editable: true,
      minWidth: 180,
      flex: 1.1,
      sortable: false,
      renderEditCell: (params) => renderEditConversionDateCell(params, 'YYYY'),
      renderCell: (params) => CustomConversionDateParcelCell(params, stackedParcelsRequestsRef, t),
      cellClassName: (params) => (params.row.parentId.length > 1 ? 'parcel_children' : ''),
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'activityEnd'}
          name={t('column.activityEndDate')}
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('common.actions'),
      flex: 1,
      minWidth: 160,
      headerAlign: 'center',
      align: 'center',
      cellClassName: 'actions',
      getActions: (params) => findRightActions(params),
      renderHeader: () => <CustomHeaderWithoutSort name={t('common.actions')} />,
    },
  ];

  const buildColumnsDefinition = () => {
    previousCultureRange({ reversed: true }).forEach((offset) => {
      columnsDefinition.push({
        field: `previousCulture${offset}`,
        headerName: forecastCampaign
          ? `${forecastCampaign.startYear - offset}-${forecastCampaign.endYear - offset}`
          : undefined,
        headerClassName: 'forecast-header',
        headerAlign: 'center',
        align: 'center',
        minWidth: 180,
        flex: 1,
        sortable: false,
        editable: true,
        type: 'singleSelect',
        cellClassName: (params) => (params.row.parentId.length > 1 ? 'parcel_children' : ''),
        valueOptions: (params) => {
          if (cultures) {
            if (params.row.previousCulture1 === t('message.noCultureAssociated')) {
              return [
                ...cultures?.map((culture) => culture.name),
                t('message.noCultureAssociated'),
              ];
            }
            return [...cultures?.map((culture) => culture.name)];
          }
          return [];
        },
        renderCell: (params) =>
          CustomParcelCell(params, stackedParcelsRequestsRef, forecastCampaign),
        renderHeader: () => (
          <CustomSortableHeader
            setSortModel={setSortModel}
            fieldName={`previousCulture${offset}`}
            name={
              forecastCampaign
                ? `${forecastCampaign.startYear - offset}-${forecastCampaign.endYear - offset}`
                : t('column.cultureN')
            }
          />
        ),
      });
    });
    return columnsDefinition;
  };

  const processedColumns = processColumns(buildColumnsDefinition(), columnsMetadata);

  const handleRowUpdateValidation = (row, errorsFormDefault) => {
    return new Promise((resolve, reject) => {
      if (errorsFormDefault.formError === false) {
        resolve(row);
      } else {
        reject();
      }
    });
  };

  /**
   * Empile les champs modifiés d'une ligne dans stackedParcelsRequestsRef
   * (payload envoyé à updateParcel au clic sur "Enregistrer").
   * @param {GridRowModel} updatedRow
   * @param {GridRowModel} previousRow
   * @returns {Promise<GridRowModel>}
   */
  const processRowUpdate = useCallback(
    async (updatedRow, previousRow) => {
      errorsFormDefault.formError = false;
      if (typeof updatedRow.surface === 'object') {
        updatedRow.surface = updatedRow.surface.surface;
      }
      const toUpdate = getOnlyAttributeUpdated(previousRow, updatedRow);

      // Logic to check if we can update the organicState
      // If the previous organicState is not the same than the updated one
      if (previousRow.organicState !== updatedRow.organicState) {
        // If the updated organicState is not organic
        // And if the previous organicState is not conventional or conversion
        if (
          updatedRow.organicState !== ORGANIC_STATES.organic &&
          (previousRow.organicState === ORGANIC_STATES.conventional ||
            previousRow.organicState === ORGANIC_STATES.conversion)
        ) {
          delete toUpdate.organicState;
        }
      }
      setErrorsFormDefault({
        ...validateDatagridData(toUpdate, errorsFormDefault, processedColumns, updatedRow.id),
      });

      if (errorsFormDefault.formError && Object.keys(rowModesModel).length > 1) {
        resetErrorsForm(errorsFormDefault);
        return previousRow;
      }

      if (Object.entries(toUpdate).length > 0) {
        await handleRowUpdateValidation(updatedRow, errorsFormDefault);

        let existInStackedRequest = false;
        stackedParcelsRequestsRef.current.forEach((request) => {
          if (request?.id === updatedRow.id) {
            existInStackedRequest = true;
          }
        });

        if (!existInStackedRequest) {
          stackedParcelsRequestsRef.current.push({ ...toUpdate, id: updatedRow.id });
        }

        stackedParcelsRequestsRef.current = stackedParcelsRequestsRef.current.map((request) => {
          if (request?.id === updatedRow.id) {
            return {
              ...request,
              ...toUpdate,
            };
          } else {
            return request;
          }
        });

        Object.keys(toUpdate).forEach((field) => {
          if (isSameAsOriginal(field, parcels, updatedRow)) {
            stackedParcelsRequestsRef.current = stackedParcelsRequestsRef.current.map((request) => {
              if (request?.id === updatedRow.id) {
                delete request[field];
                delete toUpdate[field];
                return {
                  ...request,
                  ...toUpdate,
                };
              } else {
                return request;
              }
            });
          }
        });

        stackedParcelsRequestsRef.current = stackedParcelsRequestsRef.current.filter(
          (request) => Object.keys(request).length > 1,
        );

        // trigger warning if no conversionDate is defined and parcel is going to be organic after update
        const { conversionDate, organicState } = toUpdate || {};
        const { conversionDate: stackedConversionDate, organicState: stackedOrganicState } =
          stackedParcelsRequestsRef?.current?.find((request) => request?.id === updatedRow?.id) ||
          {};

        const previousConversionDate = parcels?.find(
          (parcel) => parcel.id === updatedRow.id,
        )?.conversionDate;

        const isConversionDateMissing =
          !conversionDate && !stackedConversionDate && !previousConversionDate;

        const isBecomingOrganic =
          organicState === ORGANIC_STATES.organic || stackedOrganicState === ORGANIC_STATES.organic;
        const isConversionOrOrganicInUpdate =
          'conversionDate' in toUpdate || 'organicState' in toUpdate;

        if (isConversionDateMissing && isBecomingOrganic && isConversionOrOrganicInUpdate) {
          toast.warning(t('message.autoTransitionDate'));
        }

        setIsAllowedSubmit?.(stackedParcelsRequestsRef.current.length ? true : false);

        const updatedSurface = Number(updatedRow.surface);
        const totalSurface = updatedRow?.isParent
          ? childrenTotalSurface(updatedRow?.id, parcels) + updatedSurface
          : updatedSurface;

        updatedRow.surface = updatedSurface;
        updatedRow.totalSurface = totalSurface;

        setRows(rows.map((row) => (row.id === updatedRow.id ? updatedRow : row)));

        return updatedRow;
      }

      return previousRow;
    },
    [parcels, rowModesModel],
  );

  return (
    rows && (
      <div className="parcel_datagrid_container">
        <CustomDataGrid
          rows={rows}
          columns={processedColumns}
          processRowUpdate={processRowUpdate}
          loading={dataGridLoading}
          sortModel={sortModel}
          rowModesModel={rowModesModel}
          setRowModesModel={setRowModesModel}
          fromParcel={true}
          columnVisibilityModel={columnsVisible}
          setColumnVisibilityModel={setColumnsVisible}
          openOrganicStateDialog={openOrganicStateDialog}
          customToolbar={customToolbar}
        />
        {renderDeleteDialog()}
        {renderDeletegroupingDialog()}
        {renderOrganicStateModal()}
      </div>
    )
  );
};

export default DataGridParcels;
